import { useState, useEffect } from 'react';
import {
  Trophy, Crown, Medal, Star, Zap, TrendingUp, TrendingDown,
  Minus, User, Calendar, Target, Flame, Award, ChevronUp, ChevronDown
} from 'lucide-react';
import useUserStore from '../store/userStore';

// Mock leaderboard data for demo
const MOCK_LEADERBOARD = [
  { id: 1, username: 'system_sage', design_points: 15420, challenges_completed: 45, rank: 1, change: 0, streak: 28, avatar_color: 'from-yellow-400 to-amber-500' },
  { id: 2, username: 'arch_master', design_points: 12850, challenges_completed: 38, rank: 2, change: 1, streak: 14, avatar_color: 'from-gray-400 to-slate-500' },
  { id: 3, username: 'scale_ninja', design_points: 11200, challenges_completed: 35, rank: 3, change: -1, streak: 7, avatar_color: 'from-amber-600 to-orange-600' },
  { id: 4, username: 'db_wizard', design_points: 9800, challenges_completed: 31, rank: 4, change: 2, streak: 21, avatar_color: 'from-blue-500 to-indigo-600' },
  { id: 5, username: 'cache_king', design_points: 8500, challenges_completed: 28, rank: 5, change: 0, streak: 5, avatar_color: 'from-green-500 to-emerald-600' },
  { id: 6, username: 'load_balancer', design_points: 7200, challenges_completed: 24, rank: 6, change: -2, streak: 3, avatar_color: 'from-purple-500 to-violet-600' },
  { id: 7, username: 'queue_master', design_points: 6100, challenges_completed: 20, rank: 7, change: 3, streak: 10, avatar_color: 'from-pink-500 to-rose-600' },
  { id: 8, username: 'api_architect', design_points: 5500, challenges_completed: 18, rank: 8, change: 1, streak: 4, avatar_color: 'from-cyan-500 to-teal-600' },
  { id: 9, username: 'micro_services', design_points: 4800, challenges_completed: 16, rank: 9, change: -1, streak: 2, avatar_color: 'from-red-500 to-rose-600' },
  { id: 10, username: 'cloud_crafter', design_points: 4200, challenges_completed: 14, rank: 10, change: 0, streak: 6, avatar_color: 'from-indigo-500 to-purple-600' },
];

const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="relative">
        <Crown className="w-8 h-8 text-yellow-500 animate-bounce" />
        <div className="absolute inset-0 animate-ping">
          <Crown className="w-8 h-8 text-yellow-400 opacity-40" />
        </div>
      </div>
    );
  }
  if (rank === 2) {
    return <Medal className="w-7 h-7 text-gray-400" />;
  }
  if (rank === 3) {
    return <Medal className="w-7 h-7 text-amber-600" />;
  }
  return (
    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center font-bold text-slate-300">
      {rank}
    </div>
  );
};

const RankChange = ({ change }) => {
  if (change > 0) {
    return (
      <div className="flex items-center gap-0.5 text-green-500">
        <TrendingUp className="w-4 h-4" />
        <span className="text-xs font-medium">+{change}</span>
      </div>
    );
  }
  if (change < 0) {
    return (
      <div className="flex items-center gap-0.5 text-red-500">
        <TrendingDown className="w-4 h-4" />
        <span className="text-xs font-medium">{change}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center text-gray-400">
      <Minus className="w-4 h-4" />
    </div>
  );
};

const LeaderboardRow = ({ entry, isCurrentUser, animationDelay }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getRowStyle = () => {
    if (entry.rank === 1) return 'bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 border-yellow-300';
    if (entry.rank === 2) return 'bg-gradient-to-r from-gray-50 via-slate-50 to-gray-50 border-gray-300';
    if (entry.rank === 3) return 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-amber-300';
    if (isCurrentUser) return 'bg-gradient-to-r from-primary-900/30 via-blue-900/30 to-primary-900/30 border-primary-600';
    return 'bg-slate-800/50 border-slate-700 hover:bg-slate-700/50';
  };

  return (
    <div
      className={`
        border-2 rounded-xl mb-3 overflow-hidden transition-all duration-300 animate-slide-up
        ${getRowStyle()}
        ${isCurrentUser ? 'ring-2 ring-primary-400 ring-offset-2' : ''}
      `}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      <div
        className="p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-4">
          {/* Rank */}
          <div className="w-12 flex justify-center">
            <RankBadge rank={entry.rank} />
          </div>

          {/* Avatar & Username */}
          <div className="flex items-center gap-3 flex-1">
            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${entry.avatar_color} flex items-center justify-center text-white font-bold shadow-md`}>
              {entry.username.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-bold text-white flex items-center gap-2">
                {entry.username}
                {isCurrentUser && (
                  <span className="text-xs px-2 py-0.5 bg-primary-900/50 text-primary-400 rounded-full">
                    You
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-2">
                <span>{entry.challenges_completed} challenges</span>
                {entry.streak > 0 && (
                  <span className="flex items-center gap-0.5 text-orange-500">
                    <Flame className="w-3 h-3" />
                    {entry.streak}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Points */}
          <div className="text-right">
            <div className="font-bold text-lg text-white flex items-center gap-1 justify-end">
              <Zap className="w-4 h-4 text-yellow-500" />
              {entry.design_points.toLocaleString()}
            </div>
            <div className="text-xs text-slate-400">Design Points</div>
          </div>

          {/* Rank Change */}
          <div className="w-16 flex justify-center">
            <RankChange change={entry.change} />
          </div>

          {/* Expand Icon */}
          <div className="text-slate-400">
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
      </div>

      {/* Expanded Stats */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-700 animate-slide-up">
          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="text-center p-3 bg-slate-700/50 rounded-lg">
              <Trophy className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
              <div className="font-bold text-white">{entry.challenges_completed}</div>
              <div className="text-xs text-slate-400">Completed</div>
            </div>
            <div className="text-center p-3 bg-slate-700/50 rounded-lg">
              <Target className="w-5 h-5 text-blue-500 mx-auto mb-1" />
              <div className="font-bold text-white">{Math.round(entry.design_points / (entry.challenges_completed || 1))}</div>
              <div className="text-xs text-slate-400">Avg Score</div>
            </div>
            <div className="text-center p-3 bg-slate-700/50 rounded-lg">
              <Flame className="w-5 h-5 text-orange-500 mx-auto mb-1" />
              <div className="font-bold text-white">{entry.streak}</div>
              <div className="text-xs text-slate-400">Day Streak</div>
            </div>
            <div className="text-center p-3 bg-slate-700/50 rounded-lg">
              <Award className="w-5 h-5 text-purple-500 mx-auto mb-1" />
              <div className="font-bold text-white">{Math.floor(entry.challenges_completed / 5)}</div>
              <div className="text-xs text-slate-400">Achievements</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TimeFilter = ({ selected, onChange }) => {
  const options = [
    { value: 'all', label: 'All Time' },
    { value: 'month', label: 'This Month' },
    { value: 'week', label: 'This Week' },
    { value: 'today', label: 'Today' },
  ];

  return (
    <div className="flex bg-slate-700/50 rounded-lg p-1">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            selected === option.value
              ? 'bg-slate-600 shadow text-primary-400'
              : 'text-slate-300 hover:text-white'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

const Leaderboard = () => {
  const { user } = useUserStore();
  const [timeFilter, setTimeFilter] = useState('all');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState(null);

  useEffect(() => {
    loadLeaderboard();
  }, [timeFilter]);

  const loadLeaderboard = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));

    // Add current user to leaderboard if in demo mode
    let data = [...MOCK_LEADERBOARD];
    if (user) {
      const userEntry = {
        id: user.id,
        username: user.username,
        design_points: user.design_points || 500,
        challenges_completed: 2,
        rank: 15,
        change: 5,
        streak: 1,
        avatar_color: 'from-primary-500 to-secondary-500',
      };
      // Insert user at appropriate position
      data.push(userEntry);
      data.sort((a, b) => b.design_points - a.design_points);
      data = data.map((entry, index) => ({ ...entry, rank: index + 1 }));
      setUserRank(data.find(e => e.id === user.id)?.rank);
    }

    setLeaderboard(data.slice(0, 15));
    setLoading(false);
  };

  const topThree = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Global Leaderboard</h2>
              <p className="text-purple-200 text-sm">Compete with architects worldwide</p>
            </div>
          </div>

          {userRank && (
            <div className="bg-white/20 backdrop-blur rounded-xl px-5 py-3 text-center">
              <div className="text-3xl font-bold">#{userRank}</div>
              <div className="text-sm text-purple-200">Your Rank</div>
            </div>
          )}
        </div>

        <TimeFilter selected={timeFilter} onChange={setTimeFilter} />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Top 3 Podium */}
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700 shadow-sm p-6">
            <h3 className="font-bold text-white mb-6 text-center">Top Architects</h3>
            <div className="flex items-end justify-center gap-4">
              {/* Second Place */}
              {topThree[1] && (
                <div className="flex flex-col items-center animate-slide-up" style={{ animationDelay: '100ms' }}>
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${topThree[1].avatar_color} flex items-center justify-center text-white text-xl font-bold shadow-lg mb-2`}>
                    {topThree[1].username.charAt(0).toUpperCase()}
                  </div>
                  <Medal className="w-8 h-8 text-gray-400 mb-1" />
                  <div className="font-bold text-white text-sm">{topThree[1].username}</div>
                  <div className="text-xs text-slate-400">{topThree[1].design_points.toLocaleString()} DP</div>
                  <div className="w-20 h-24 bg-gradient-to-t from-slate-600 to-slate-700 rounded-t-lg mt-2 flex items-end justify-center pb-2">
                    <span className="text-2xl font-bold text-slate-400">2</span>
                  </div>
                </div>
              )}

              {/* First Place */}
              {topThree[0] && (
                <div className="flex flex-col items-center animate-slide-up">
                  <Crown className="w-10 h-10 text-yellow-500 animate-bounce mb-1" />
                  <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${topThree[0].avatar_color} flex items-center justify-center text-white text-2xl font-bold shadow-xl ring-4 ring-yellow-400 mb-2`}>
                    {topThree[0].username.charAt(0).toUpperCase()}
                  </div>
                  <div className="font-bold text-white">{topThree[0].username}</div>
                  <div className="text-sm text-slate-400">{topThree[0].design_points.toLocaleString()} DP</div>
                  <div className="w-24 h-32 bg-gradient-to-t from-yellow-600 to-yellow-500 rounded-t-lg mt-2 flex items-end justify-center pb-2">
                    <span className="text-3xl font-bold text-yellow-200">1</span>
                  </div>
                </div>
              )}

              {/* Third Place */}
              {topThree[2] && (
                <div className="flex flex-col items-center animate-slide-up" style={{ animationDelay: '200ms' }}>
                  <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${topThree[2].avatar_color} flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2`}>
                    {topThree[2].username.charAt(0).toUpperCase()}
                  </div>
                  <Medal className="w-7 h-7 text-amber-600 mb-1" />
                  <div className="font-bold text-white text-sm">{topThree[2].username}</div>
                  <div className="text-xs text-slate-400">{topThree[2].design_points.toLocaleString()} DP</div>
                  <div className="w-16 h-16 bg-gradient-to-t from-amber-700 to-amber-600 rounded-t-lg mt-2 flex items-end justify-center pb-2">
                    <span className="text-xl font-bold text-amber-200">3</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Rest of Leaderboard */}
          <div className="space-y-2">
            {rest.map((entry, index) => (
              <LeaderboardRow
                key={entry.id}
                entry={entry}
                isCurrentUser={user && entry.id === user.id}
                animationDelay={index * 50}
              />
            ))}
          </div>

          {/* Your Position Card (if not in top 15) */}
          {userRank && userRank > 15 && (
            <div className="bg-gradient-to-r from-primary-900/30 to-blue-900/30 rounded-xl border-2 border-primary-600 p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 flex justify-center">
                  <div className="w-10 h-10 rounded-full bg-primary-900/50 flex items-center justify-center font-bold text-primary-400">
                    {userRank}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold">
                    {user?.username?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold text-white">{user?.username} (You)</div>
                    <div className="text-xs text-slate-400">Keep climbing!</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-lg text-white">{user?.design_points} DP</div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Leaderboard;
